import { useTranslation } from "@/hooks";
import { useUpdateProfile } from "@/services";
import { currentUserAtom } from "@/store/login";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form, Icon, Input } from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { type FC, useMemo, useState } from "react";
import type { ControllerRenderProps } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SettingsCard } from "./SettingsCard";
import { SettingsField } from "./SettingsRow";

const createChangePasswordSchema = (
	t: ReturnType<typeof useTranslation>["t"],
) =>
	z.object({
		currentPassword: z.string().min(1),
		newPassword: z
			.string()
			.min(8)
			.max(64)
			.regex(
				/^(?=.*[a-z])(?=.*[A-Z]).+$/,
				t("validation.containUppercaseAndLowercase", {
					field: t("profile.changePassword.newLabel"),
				}),
			)
			.regex(
				/^(?=.*\d).+$/,
				t("validation.containNumber", {
					field: t("profile.changePassword.newLabel"),
				}),
			),
	});

export type ChangePasswordFormData = z.infer<
	ReturnType<typeof createChangePasswordSchema>
>;

const PasswordInput: FC<{
	field: ControllerRenderProps<
		ChangePasswordFormData,
		"currentPassword" | "newPassword"
	>;
	placeholder: string;
}> = ({ field, placeholder }) => {
	const [visible, setVisible] = useState(false);
	return (
		<Input
			{...field}
			value={field.value ?? ""}
			ltr
			type={visible ? "text" : "password"}
			placeholder={placeholder}
			autoComplete={
				field.name === "currentPassword" ? "current-password" : "new-password"
			}
			append={
				<Button
					type="button"
					icon
					color="ghost"
					size="sm"
					aria-label={visible ? "Hide password" : "Show password"}
					onClick={() => setVisible((v) => !v)}
				>
					<Icon name={visible ? "eye-off-line" : "eye-line"} size="sm" />
				</Button>
			}
		/>
	);
};

export const SettingsPasswordCard: FC = () => {
	const { t, lang, i18n } = useTranslation();
	const user = useAtomValue(currentUserAtom);
	const updateProfileMutation = useUpdateProfile();
	const changePasswordSchema = useMemo(
		() => createChangePasswordSchema(i18n.getFixedT(lang)),
		[i18n, lang],
	);

	const form = useForm<ChangePasswordFormData>({
		resolver: zodResolver(changePasswordSchema),
		defaultValues: { currentPassword: "", newPassword: "" },
	});

	const onSubmit = form.handleSubmit((data) => {
		updateProfileMutation.mutate(
			{
				path: { user_id: user?.id || "" },
				body: {
					display_name: null,
					password_change: {
						current_password: data.currentPassword,
						new_password: data.newPassword,
					},
				},
			},
			{ onSuccess: () => form.reset() },
		);
	});

	return (
		<Form {...form}>
			<form noValidate onSubmit={onSubmit}>
				<SettingsCard
					layout="compact"
					title={t("profile.changePassword.title")}
					description={t("settings.passwordDescription")}
					footer={
						<Button type="submit" loading={updateProfileMutation.isPending}>
							{t("settings.saveButton")}
						</Button>
					}
				>
					<SettingsField
						control={form.control}
						name="currentPassword"
						title={t("profile.changePassword.currentLabel")}
						render={(field) => (
							<PasswordInput
								field={field}
								placeholder={t("profile.changePassword.currentLabel")}
							/>
						)}
					/>
					<SettingsField
						control={form.control}
						name="newPassword"
						title={t("profile.changePassword.newLabel")}
						render={(field) => (
							<PasswordInput
								field={field}
								placeholder={t("profile.changePassword.newLabel")}
							/>
						)}
					/>
				</SettingsCard>
			</form>
		</Form>
	);
};
